import { ref } from 'vue';
import { fetchInboxSummary } from '@/api/workflowApi';
import { isAnnualAppraisalsEnabled } from '@/constants/featureFlags';

function normalizeSummaryPayload(data) {
  if (isAnnualAppraisalsEnabled()) {
    const count = Number(data?.pending_item_count ?? 0);
    return {
      pendingCount: Number.isFinite(count) ? Math.max(0, count) : 0,
      breakdown:
        data?.breakdown && typeof data.breakdown === 'object' ? data.breakdown : {}
    };
  }

  const breakdown =
    data?.breakdown && typeof data.breakdown === 'object'
      ? { ...data.breakdown }
      : {};
  const appraisalCount = Number(breakdown.annual_appraisal ?? 0);
  delete breakdown.annual_appraisal;

  const rawCount = Number(data?.pending_item_count ?? 0);
  const pendingCount = Number.isFinite(rawCount)
    ? Math.max(0, rawCount - (Number.isFinite(appraisalCount) ? appraisalCount : 0))
    : Object.values(breakdown).reduce(
        (sum, value) => sum + (Number.isFinite(Number(value)) ? Number(value) : 0),
        0
      );

  return { pendingCount, breakdown };
}

const pendingCount = ref(0);
const breakdown = ref({});
const loadFailed = ref(false);

let inflightPromise = null;
let pendingInvalidation = false;

async function runRefresh() {
  inflightPromise = (async () => {
    try {
      const data = await fetchInboxSummary();
      const normalized = normalizeSummaryPayload(data);
      pendingCount.value = normalized.pendingCount;
      breakdown.value = normalized.breakdown;
      loadFailed.value = false;
    } catch (error) {
      loadFailed.value = true;
      console.warn('Failed to load inbox summary:', error);
    } finally {
      inflightPromise = null;
      if (pendingInvalidation) {
        pendingInvalidation = false;
        await runRefresh();
      }
    }
  })();
  return inflightPromise;
}

/**
 * Module-level inbox summary state (sidebar badge + action invalidation).
 */
export function useInboxSummary() {
  async function refresh() {
    if (inflightPromise) {
      pendingInvalidation = true;
      return inflightPromise;
    }
    return runRefresh();
  }

  function invalidate() {
    pendingInvalidation = true;
    if (!inflightPromise) {
      refresh();
    }
  }

  return {
    pendingCount,
    breakdown,
    loadFailed,
    refresh,
    invalidate
  };
}
